/*
 * ********************************************************************
 *  GlobePayroll Confidential
 *
 *  COPYRIGHT ©2016 by GlobePayroll. All Rights Reserved.
 *
 *   NOTICE:  All information contained herein is, and remains the property of GlobePayroll.
 *   The intellectual and technical concepts contained herein are proprietary to GlobePayroll
 *   and may be covered by U.S. and Foreign Patents, patents in process, and are protected by
 *   trade secret or copyright law.
 *   Dissemination of this information or reproduction of this material is strictly forbidden
 *   unless prior written permission is obtained from GlobePayroll.
 *  ********************************************************************
 *
 *  @author estelle.giuly
 *
 */

import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';

import {BehaviorSubject} from 'rxjs';
import {filter, takeUntil, tap} from 'rxjs/operators';

import {Destroyable} from '../../../destroyable.component';
import {FormModel, FormOutcome, Variable} from '../workflow-v2.interface';
import {FORM_OUTCOME_TRANSLATE_PREFIX} from '../workflow-v2.service';

export const isUserExpressionField = (field) => field.params && field.params['type'] && field.params['type'] === 'user';

@Component({
  selector: 'gp-workflow-form',
  templateUrl: './workflow-form.component.html',
  styleUrls: ['./workflow-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WorkflowFormComponent extends Destroyable implements OnInit, OnChanges {

  public readonly FORM_OUTCOME_TRANSLATE_PREFIX = FORM_OUTCOME_TRANSLATE_PREFIX;

  @Input()
  public formModel: FormModel;

  @Input()
  public taskDefinitionKey: string;

  @Output()
  public cancelFormOutcome: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  private submitFormOutcome: EventEmitter<FormOutcome> = new EventEmitter();

  public formGroup: UntypedFormGroup;

  public updatedRequest: Variable;

  public taskError: string;

  public descriptorLoading = true;

  private outComeSubject$ = new BehaviorSubject<FormOutcome>(null);

  constructor(private formBuilder: UntypedFormBuilder,
              private cd: ChangeDetectorRef) {
    super();
  }

  public ngOnChanges() {
    this.taskError = null;
    this.descriptorLoading = true;
  }

  public ngOnInit() {
    this.createForm();
    this.initOutComeObservable();
  }

  public createForm() {
    const builderGroup = {};
    this.formModel.fields.forEach(field => {
      builderGroup[field.id] = ['', ''];
    });
    this.formGroup = this.formBuilder.group(builderGroup);
  }

  private initOutComeObservable() {
    this.outComeSubject$.pipe(
      filter((outcome) => outcome !== null),
      tap((outcome) => {
        if (this.updatedRequest) {
          outcome.updatedRequest = this.updatedRequest;
        }
        this.submitFormOutcome.emit(outcome);
      }),
      takeUntil(this.componentDestroyed$))
      .subscribe();
  }

  public onSubmitForm(outcome?: FormOutcome) {
    this.outComeSubject$.next(outcome);
  }

  public close(): void {
    this.cancelFormOutcome.emit();
  }

  public onRequestUpdate(updatedRequest: Variable) {
    this.updatedRequest = updatedRequest;
  }

  public setError(event: string) {
    this.taskError = event;
  }

  public setLoading() {
    this.descriptorLoading = false;
    this.cd.detectChanges();
  }

}
